import { useQuery } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { Search, XCircle } from 'lucide-react';
import type { ChangeEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import { ArticleType } from '../KnowledgeManager.dto';
import { DataTableFacetedFilter } from './data-table-faceted-filter';
import { DataTableTextFilter } from './data-table-text-filter';
import { DataTableViewOptions } from './data-table-view-options';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { cn } from '@/Domain/utils/cn';

const TAG_SEARCH_MIN_CHARS = 2;
const TAG_SEARCH_DEBOUNCE_MS = 250;
const TAG_SEARCH_LIMIT = 50;

type Props<TData> = {
  table: Table<TData>;
  onRefreshAll?: () => void;
  refreshAllDisabled?: boolean;
};

const articleTypeOptions: { value: ArticleType; label: string }[] = Object.values(ArticleType).map(
  (value) => ({
    value,
    label: value,
  }),
);

export const DataTableToolbar = <TData,>({
  table,
  onRefreshAll,
  refreshAllDisabled = false,
}: Props<TData>) => {
  const { t } = useTranslation('knowledge_base');
  const isFiltered = table.getState().columnFilters.length > 0;
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const globalFilterValue = (table.getState().globalFilter as string) ?? '';

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(event.target.value);
  };

  const typeColumn = table.getColumn('type');
  const tagsColumn = table.getColumn('tags');
  const titleColumn = table.getColumn('title');
  const urlColumn = table.getColumn('url');

  const [isTagsFilterOpen, setIsTagsFilterOpen] = useState(false);
  const [tagsSearchInput, setTagsSearchInput] = useState('');
  const [debouncedTagsQuery] = useDebounceValue(tagsSearchInput, TAG_SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedTagsQuery.trim();
  const isServerSearchActive = trimmedQuery.length >= TAG_SEARCH_MIN_CHARS;

  const { data: topTags = [], isLoading: isTagsLoading } = useQuery<string[]>({
    queryKey: [QueryKey.knowledgeBase.tags],
    enabled: Boolean(tagsColumn) && isTagsFilterOpen,
    staleTime: 5 * 60 * 1000,
  });

  const { data: searchedTags = [], isFetching: isSearchFetching } = useQuery<string[]>({
    queryKey: [QueryKey.knowledgeBase.tagsSearch, trimmedQuery, TAG_SEARCH_LIMIT],
    enabled: Boolean(tagsColumn) && isTagsFilterOpen && isServerSearchActive,
    staleTime: 60 * 1000,
  });

  const tagOptions = useMemo(() => {
    const source = isServerSearchActive ? searchedTags : topTags;
    return source.map((tag) => ({ label: tag, value: tag }));
  }, [isServerSearchActive, searchedTags, topTags]);

  const isTagsBusy = isServerSearchActive ? isSearchFetching : isTagsLoading;
  const tagsEmptyState = isTagsBusy
    ? t('articles.list.filters.tags_loading', { defaultValue: 'Loading tags…' })
    : isServerSearchActive
      ? t('articles.list.filters.tags_no_matches', { defaultValue: 'No matching tags' })
      : t('articles.list.filters.tags_empty', { defaultValue: 'No tags assigned to articles' });

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
        <Input
          data-testid="article-search"
          placeholder={t('articles.list.filters.search_placeholder', {
            defaultValue: 'Search articles...',
          })}
          value={globalFilterValue}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          onChange={handleSearchChange}
          size="sm"
          className={cn(
            'flex-shrink-0 transition-all duration-200 ease-out',
            isSearchFocused || globalFilterValue
              ? 'w-full sm:w-[300px] lg:w-[400px]'
              : 'w-full sm:w-[200px]',
          )}
          startIcon={<Search className="h-4 w-4" />}
        />
        {titleColumn && (
          <DataTableTextFilter
            column={titleColumn}
            title={t('articles.list.filters.title')}
            placeholder={t('articles.list.filters.title_placeholder')}
            clearLabel={t('articles.list.filters.clear')}
          />
        )}
        {urlColumn && (
          <DataTableTextFilter
            column={urlColumn}
            title={t('articles.list.filters.url')}
            placeholder={t('articles.list.filters.url_placeholder')}
            clearLabel={t('articles.list.filters.clear')}
          />
        )}
        {typeColumn && (
          <DataTableFacetedFilter
            column={typeColumn}
            title={t('articles.list.filters.type')}
            options={articleTypeOptions}
          />
        )}
        {tagsColumn && (
          <DataTableFacetedFilter
            column={tagsColumn}
            title={t('articles.list.filters.tags')}
            options={tagOptions}
            emptyState={tagsEmptyState}
            enableSearch
            searchPlaceholder={t('articles.list.filters.tags_search_placeholder')}
            open={isTagsFilterOpen}
            onOpenChange={setIsTagsFilterOpen}
            searchQuery={tagsSearchInput}
            onSearchQueryChange={setTagsSearchInput}
          />
        )}
        {isFiltered && (
          <Button
            data-testid="article-filters-reset"
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="px-2 text-red-600"
            size="sm"
          >
            {t('articles.list.filters.reset')}
            <XCircle className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onRefreshAll && (
          <TooltipProvider>
            <Tooltip>
              {/* Span wrapper: disabled buttons set pointer-events: none, so
                  Radix's tooltip trigger never fires — the span is the actual
                  hover target while the inner button stays semantically
                  disabled. */}
              <TooltipTrigger asChild>
                <span tabIndex={refreshAllDisabled ? 0 : -1} className="inline-flex">
                  <Button
                    variant="primary_dark_light"
                    size="sm"
                    onClick={onRefreshAll}
                    disabled={refreshAllDisabled}
                    className="flex items-center gap-2"
                    data-testid="refresh-all-urls-button"
                  >
                    {t('articles.list.actions.refresh_all')}
                  </Button>
                </span>
              </TooltipTrigger>
              {refreshAllDisabled && (
                <TooltipContent>
                  {t('articles.list.actions.refresh_all_disabled_tooltip')}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
        <DataTableViewOptions table={table} />
      </div>
    </div>
  );
};
